import { AddIcon } from "@chakra-ui/icons";
import {
  Badge,
  Box,
  Button,
  Flex,
  Icon,
  IconButton,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { FaClipboardList, FaEdit, FaTrash } from "react-icons/fa";
import type { SelectionCriterionDto } from "../../../../../autogen/bff-api";
import { TipTap, isTiptapContentEmpty } from "../../../../../common/input/TipTap/TipTap";
import { SELECTION_CRITERION_TYPE_OPTIONS } from "../utils/publicProcurementOptions";

interface Props {
  items: SelectionCriterionDto[];
  onAdd: () => void;
  onEdit: (index: number) => void;
  onDelete: (index: number) => void;
}

// t('No requirements added yet') t('Add qualification requirements or choose a template')
// t('Add requirement') t('Requires evidence')
// t('Edit requirement') t('Delete requirement')

export const QualificationRequirementList = ({ items, onAdd, onEdit, onDelete }: Props) => {
  const { t } = useTranslation();

  if (items.length === 0) {
    return (
      <Box
        borderWidth="1px"
        borderColor="smBorder"
        borderRadius="lg"
        bg="smBackgroundSecondary"
        py="12"
        px="6"
        textAlign="center"
      >
        <Icon as={FaClipboardList} boxSize="10" color="smMuted" mb="4" />
        <Text fontWeight="bold" mb="1">
          {t("No requirements added yet")}
        </Text>
        <Text color="smMuted" fontSize="sm" mb="6">
          {t("Add qualification requirements or choose a template")}
        </Text>
        <Button
          colorScheme="teal"
          leftIcon={<AddIcon />}
          onClick={onAdd}
        >
          {t("Add requirement")}
        </Button>
      </Box>
    );
  }

  return (
    <VStack spacing="3" align="stretch">
      {items.map((item, index) => {
        const typeOption = SELECTION_CRITERION_TYPE_OPTIONS.find(
          (o) => o.value === item.type
        );

        return (
          <Box
            key={`${item.type}-${item.name}-${index}`}
            data-testid="requirement-row"
            borderWidth="1px"
            borderColor="smBorder"
            borderRadius="lg"
            p="4"
            role="group"
            _hover={{ borderColor: "smMuted" }}
          >
            <Flex justify="space-between" align="flex-start">
              <Box flex="1" minW="0">
                <Flex gap="2" mb="2" flexWrap="wrap">
                  {typeOption && (
                    <Badge
                      bg={typeOption.badgeBg}
                      color={typeOption.badgeColor}
                      px="2"
                      py="0.5"
                      borderRadius="md"
                      fontWeight="medium"
                      fontSize="xs"
                      textTransform="none"
                    >
                      {t(typeOption.value)}
                    </Badge>
                  )}
                  {item.requiresEvidence && (
                    <Badge
                      variant="outline"
                      borderColor="smBorder"
                      color="smMuted"
                      px="2"
                      py="0.5"
                      borderRadius="md"
                      fontWeight="medium"
                      fontSize="xs"
                      textTransform="none"
                    >
                      {t("Requires evidence")}
                    </Badge>
                  )}
                </Flex>
                <Text fontWeight="bold" mb="1">
                  {item.name}
                </Text>
                {!isTiptapContentEmpty(item.description) && (
                  <Box color="smMuted" fontSize="sm">
                    <TipTap content={item.description} editable={false} hasBorder={false} padding="0" maxHeight="" />
                  </Box>
                )}
              </Box>
              <Flex
                gap="1"
                ml="2"
                opacity={0}
                _groupHover={{ opacity: 1 }}
                _focusWithin={{ opacity: 1 }}
                transition="opacity 0.15s"
              >
                <IconButton
                  aria-label={t("Edit requirement")}
                  icon={<FaEdit />}
                  size="sm"
                  variant="ghost"
                  onClick={() => onEdit(index)}
                />
                <IconButton
                  aria-label={t("Delete requirement")}
                  icon={<FaTrash />}
                  size="sm"
                  variant="ghost"
                  colorScheme="red"
                  onClick={() => onDelete(index)}
                />
              </Flex>
            </Flex>
          </Box>
        );
      })}
    </VStack>
  );
};
